import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useQuery, useLazyQuery, useMutation, useApolloClient } from '@apollo/client';
import { useHistory, useLocation } from 'react-router-dom';
import { Chip, ExternalChip, ChipTab } from '../../lib/types';
import {
  mapMiniChipToRescueChip,
  mapResellerChipToRescueChip,
  mapPetFormToChipNewPetInput,
  mapAdopterFormToContactInfoInput,
  mapPetFormToUpdatePetInput,
  buildPetByChipGqlId,
} from '../../lib/chipAdapters';
import { useTransferPetWithChipToAdopter } from '../../lib/useTransferPetWithChipToAdopter';
import { useToast } from '../../components/Toast/ToastContext';
import { ChipCondition, ChipStatusType } from '@/types/gql-op-types';
import { chipConditionBadgeLabel } from '@/lib/chipConditions';
import {
  rescueViewingChipsQuery,
  RescueViewingChipsData,
  rescueSearchChipsQuery,
  RescueSearchChipsData,
  rescueNanoShipmentsQuery,
  RescueNanoShipmentsData,
  rescuePetsQuery,
  RescuePetsData,
  unassignDeviceMutation,
  rescueChipStatsQuery,
  updateChipConditionMutation,
  removeChipFromPetMutation,
  updateChipOwnerContactInfoMutation,
  rescueUpdatePetMutation,
} from '../../lib/rescueQueries';
import {
  resellerChipDetailsQuery,
  registerThirdPartyChip,
  recallChipMutation,
  reassignChipToResellerMutation,
} from '@/appview/ResellerChipDetails/ResellerChipDetails.graphql';
import {
  assignNewPetToChipMutation,
  assignExistingPetToChipMutation,
  assignNewOwnerToChipMutation,
} from '@/appview/RegisterChipV2/RegisterChip.graphql';
import SearchBar from '../../components/SearchBar/SearchBar';
import PortalTabs, { TabDef } from '../../components/PortalTabs/PortalTabs';
import Button from '../../components/Button/Button';
import useAdoptedPetIds from '../../hooks/useAdoptedPetIds';
import useAllDeviceItems from '../../hooks/useAllDeviceItems';
import usePendingAdoptionPetIds from '../../hooks/usePendingAdoptionPetIds';
import ChipsTable from './ChipsTable';
import OrdersTable, { isVisibleNanoShipment } from './OrdersTable';
import RegisterChipModal, { RegisterChipPayload } from './RegisterChipModal';
import TransferChipModal, { DeviceAppliedChipFailedError, DeviceTransferAction } from './TransferChipModal';
import type { AdopterFormData } from './OwnerFormFields';
import ChipDetailModal from './ChipDetailModal';
import EditChipModal, { EditChipPayload } from './EditChipModal';
import ChangeConditionModal from './ChangeConditionModal';
import RecallModal from './RecallModal';
import RemoveChipFromPetModal from './RemoveChipFromPetModal';
import ExternalChipLookup from './ExternalChipLookup';
import ExternalChipDetailModal from './ExternalChipDetailModal';
import ClaimChipModal from './ClaimChipModal';
import OrderNanosModal from './OrderNanosModal';
import ChipsEnrollModal from './ChipsEnrollModal';
import EnrollEmptyState from '../../components/EnrollEmptyState/EnrollEmptyState';
import Pagination from '../../components/Pagination/Pagination';
import {
  trackChipRegistrationStarted,
  trackChipRegistered,
  trackChipOwnerAdded,
  trackChipsOrderClicked,
  trackChipConditionUpdated,
  trackChipRemovedFromPet,
  trackDeviceTransferToAdopt,
  trackChipEdited,
} from '../../analytics';
import styles from './Chips.module.scss';
import { useRescueContext } from '@/rescue/lib/RescueContext';
import { CHIPS_PAGE_SIZE, chipListsFirstPageRefetch } from '../../lib/chipListsRefetch';

const PAGE_SIZE = CHIPS_PAGE_SIZE;
const SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 3;

const refetchChipLists = [
  ...chipListsFirstPageRefetch,
  // rescuePetsQuery powers the pet → device lookup that drives the optional
  // device-handling step. The new device + chip mutations narrow-return only
  // `{ id }` shapes, which Apollo can't use to invalidate the cached pet→device
  // relation — listing the query here keeps the next Add Owner flow from acting
  // on stale device assignments.
  { query: rescuePetsQuery },
];

function LoadingSpinner({ message }: { message?: string }) {
  return (
    <div className={styles.loadingContainer}>
      <svg className={styles.spinner} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
        <path fill="currentColor" d="M4 12a8 8 0 018-8v3a5 5 0 00-5 5H4z" />
      </svg>
      {message && <span>{message}</span>}
    </div>
  );
}

// Shown above the chip list while a rescue reseller isn't verified. Verification has no deadline —
// it needs a 501c3/government attestation or a manual review — so the copy promises no timeline.
function ChipsPendingBanner() {
  return (
    <div className={styles.pendingBanner}>
      <svg
        className={styles.pendingBannerIcon}
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </svg>
      <div>
        <div className={styles.pendingBannerTitle}>Verification pending</div>
        <div className={styles.pendingBannerText}>
          Your organization isn't verified yet, so claiming chips and viewing full adopter contact details stay locked.
          Reach out to our rescue team if you have questions.
        </div>
      </div>
    </div>
  );
}

export default function Chips() {
  const apolloClient = useApolloClient();
  const { showToast } = useToast();
  const location = useLocation();
  const history = useHistory();
  const urlSearch = new URLSearchParams(location.search).get('search') || '';
  const [searchInput, setSearchInput] = useState(urlSearch);
  const [currentTab, setCurrentTab] = useState<ChipTab>(urlSearch ? 'registered' : 'stock');
  const [debouncedQuery, setDebouncedQuery] = useState(urlSearch);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(debounceRef.current), []);
  const [stockPage, setStockPage] = useState(1);
  const [registeredPage, setRegisteredPage] = useState(1);
  const [hasExternalMatch, setHasExternalMatch] = useState(false);
  const { isVerifiedReseller, isReseller, isRescueReseller, orgInfoLoading } = useRescueContext();
  // Chip queries are reseller-only. Skip until org-info resolves and confirms the user has a
  // reseller account — otherwise we fire requests that return empty `chipReseller` blocks before
  // the gate below has a chance to render the enrollment empty state.
  const skipResellerQueries = orgInfoLoading || !isReseller;

  // Sync from URL when navigating externally (e.g. dashboard link)
  useEffect(() => {
    setSearchInput(urlSearch);
    setDebouncedQuery(urlSearch);
    setHasExternalMatch(false);
    if (urlSearch) {
      setCurrentTab('registered');
    }
  }, [urlSearch]);

  function updateUrl(value: string) {
    const params = new URLSearchParams(location.search);
    if (value) {
      params.set('search', value);
    } else {
      params.delete('search');
    }
    history.replace({ search: params.toString() });
  }

  function handleSearchChange(value: string) {
    setSearchInput(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      // Clear the External-tab boolean before the new query renders so a stale
      // "External 1" from the previous query can't briefly survive the swap.
      setHasExternalMatch(false);
      setDebouncedQuery(value.trim());
      updateUrl(value);
    }, SEARCH_DEBOUNCE_MS);
  }

  // ── GraphQL queries ─────────────────────────

  const {
    data: shippedRawData,
    previousData: shippedPreviousData,
    loading: shippedLoading,
    error: shippedError,
  } = useQuery<RescueViewingChipsData>(rescueViewingChipsQuery, {
    variables: { statusType: ChipStatusType.SHIPPED, limit: PAGE_SIZE, offset: (stockPage - 1) * PAGE_SIZE },
    fetchPolicy: 'cache-and-network',
    skip: skipResellerQueries,
  });

  const {
    data: activeRawData,
    previousData: activePreviousData,
    loading: activeLoading,
    error: activeError,
  } = useQuery<RescueViewingChipsData>(rescueViewingChipsQuery, {
    variables: { statusType: ChipStatusType.ACTIVE, limit: PAGE_SIZE, offset: (registeredPage - 1) * PAGE_SIZE },
    fetchPolicy: 'cache-and-network',
    skip: skipResellerQueries,
  });

  // Keep stale data visible during pagination transitions.
  // When offset changes (cache miss), data becomes undefined while the network
  // request is in flight.  previousData retains the old rows so the table
  // never flashes empty.
  const shippedData = shippedRawData ?? shippedPreviousData;
  const activeData = activeRawData ?? activePreviousData;

  const isSearchActive = debouncedQuery.length >= MIN_SEARCH_LENGTH;
  const searchQueryInput = useMemo(() => ({ searchTerm: debouncedQuery }), [debouncedQuery]);
  const { data: searchData, loading: searchLoading } = useQuery<RescueSearchChipsData>(rescueSearchChipsQuery, {
    variables: { input: searchQueryInput },
    skip: !isSearchActive || skipResellerQueries,
    fetchPolicy: 'cache-and-network',
  });

  const {
    data: nanoShipmentsData,
    loading: nanoShipmentsLoading,
    error: nanoShipmentsError,
  } = useQuery<RescueNanoShipmentsData>(rescueNanoShipmentsQuery, {
    fetchPolicy: 'cache-and-network',
    skip: skipResellerQueries,
  });

  const [fetchChipDetails] = useLazyQuery(resellerChipDetailsQuery);

  // Pet IDs of fully-adopted-out pets — used to hide Add Owner / Clear Owner.
  // While loading, treat every pet-bearing chip as potentially adopted so we
  // don't briefly show mutation buttons that we're about to hide.
  const { adoptedPetIds, loading: adoptedLoading } = useAdoptedPetIds();
  // Pet IDs of pets with a pending adoption transfer — used to hide Clear
  // Owner and Remove from Pet, both of which the backend rejects mid-transfer.
  // Add Owner is intentionally not gated by this; per #354 it has its own
  // pending-aware device-handoff flow.
  const { pendingAdoptionPetIds, loading: pendingAdoptionLoading } = usePendingAdoptionPetIds();

  // Lookup table for the chip's pet device — drives the optional device-handling
  // step inside TransferChipModal when the chip's pet still has a GPS device
  // held by the rescue.
  //
  // `cache-first`: only fire a network request when the cache is empty. The
  // device-handling step only runs when the user opens Add Owner on a
  // device-bearing pet — for most page loads this data is never consumed, and
  // refreshing it via `cache-and-network` on every Chips mount is wasteful for
  // rescues with large pet lists. Freshness after a mutation is maintained by
  // `refetchChipLists` (which now includes `rescuePetsQuery`) so we don't
  // serve stale device assignments to subsequent Add Owner flows.
  const { data: petsData } = useQuery<RescuePetsData>(rescuePetsQuery, {
    fetchPolicy: 'cache-first',
    skip: skipResellerQueries,
  });
  // `rescuePetsQuery` above only reaches the RESCUE household, but a pet the rescue has placed with a
  // foster lives in that foster's own household and still wears the rescue's collar. Read those from
  // `rescueDevices`, which scopes by the rescue's RESCUE *and* FOSTER households: IN_FOSTER for an open
  // placement, IN_RESCUE for a pet sitting in a foster household without one (a recalled placement, or
  // a pet moved there by hand). Without them the lookup below misses the pet, the device step never
  // renders, and Add Owner falls through to the bare chip assignment, which re-homes the pet and takes
  // the collar with it.
  const fosterDeviceItems = useAllDeviceItems('IN_FOSTER', { skip: skipResellerQueries });
  const rescueDeviceItems = useAllDeviceItems('IN_RESCUE', { skip: skipResellerQueries });
  const petByChipGqlId = useMemo(() => {
    const rescueHousehold = petsData?.currentUser?.userHouseholds?.find((uh) => uh.household.type === 'RESCUE');
    return buildPetByChipGqlId(rescueHousehold?.household.pets ?? [], [
      ...fosterDeviceItems.items,
      ...rescueDeviceItems.items,
    ]);
  }, [petsData, fosterDeviceItems.items, rescueDeviceItems.items]);

  // Pets in the rescue's own household are the only ones the rescue can edit; an owned pet has moved out.
  const rescuePetIds = useMemo(() => {
    const rescueHousehold = petsData?.currentUser?.userHouseholds?.find((uh) => uh.household.type === 'RESCUE');
    return new Set((rescueHousehold?.household.pets ?? []).map((p) => p.id));
  }, [petsData]);

  // ── GraphQL mutations ─────────────────────────

  const [assignNewPet] = useMutation(assignNewPetToChipMutation, { refetchQueries: refetchChipLists });
  const [assignExistingPet] = useMutation(assignExistingPetToChipMutation, { refetchQueries: refetchChipLists });
  const [assignNewOwner] = useMutation(assignNewOwnerToChipMutation, { refetchQueries: refetchChipLists });
  const [recallChipMut] = useMutation(recallChipMutation, { refetchQueries: refetchChipLists });
  const [registerThirdParty] = useMutation(registerThirdPartyChip, { refetchQueries: refetchChipLists });
  const [reassignToReseller] = useMutation(reassignChipToResellerMutation, { refetchQueries: refetchChipLists });
  const [removeChipFromPetMut] = useMutation(removeChipFromPetMutation, { refetchQueries: refetchChipLists });
  // Intentionally no `refetchQueries` — handleChangeConditionSubmit does a
  // broader refetch that also covers search results and the dashboard stats.
  const [updateChipCondition] = useMutation(updateChipConditionMutation);
  const [updateChipOwnerContactInfo] = useMutation(updateChipOwnerContactInfoMutation);
  const [updatePet] = useMutation(rescueUpdatePetMutation);
  const [unassignDeviceMut] = useMutation(unassignDeviceMutation, { refetchQueries: refetchChipLists });
  const transferPetWithChip = useTransferPetWithChipToAdopter({ refetchQueries: refetchChipLists });

  // ── Derived chip lists ─────────────────────────
  // SHIPPED → stock tab, ACTIVE → registered tab.

  const resellerEmail =
    searchData?.currentUser?.email ?? shippedData?.currentUser?.email ?? activeData?.currentUser?.email ?? '';

  const stockChips = useMemo(() => {
    const chips = shippedData?.currentUser?.chipReseller?.chips?.chips ?? [];
    return chips.map((c) => mapMiniChipToRescueChip(c, resellerEmail));
  }, [shippedData, resellerEmail]);

  const registeredChips = useMemo(() => {
    const chips = activeData?.currentUser?.chipReseller?.chips?.chips ?? [];
    return chips.map((c) => mapMiniChipToRescueChip(c, resellerEmail));
  }, [activeData, resellerEmail]);

  const searchChips = useMemo(() => {
    const chips = searchData?.currentUser?.chipReseller?.searchChips?.chips ?? [];
    return chips.map((c) => mapMiniChipToRescueChip(c, resellerEmail));
  }, [searchData, resellerEmail]);

  const displayedChips = useMemo(() => {
    if (isSearchActive) {
      return searchChips;
    }
    return currentTab === 'stock' ? stockChips : registeredChips;
  }, [isSearchActive, searchChips, currentTab, stockChips, registeredChips]);

  const shippedTotal = shippedData?.currentUser?.chipReseller?.chips?.metadata?.totalChipCount ?? 0;
  const activeTotal = activeData?.currentUser?.chipReseller?.chips?.metadata?.totalChipCount ?? 0;

  const nanoShipments = useMemo(
    () => (nanoShipmentsData?.rescueNanoShipments ?? []).filter(isVisibleNanoShipment),
    [nanoShipmentsData],
  );
  const ordersTotal = nanoShipments.length;

  const defaultTabs: TabDef[] = [
    { key: 'stock', label: 'In Stock', count: shippedTotal },
    { key: 'registered', label: 'Registered', count: activeTotal },
    { key: 'orders', label: 'Orders', count: ordersTotal },
  ];
  const showExternalMatchTab = hasExternalMatch && searchChips.length === 0;
  const searchTabs: TabDef[] = showExternalMatchTab
    ? [{ key: 'all', label: 'External', count: 1 }]
    : [{ key: 'all', label: 'All', count: searchChips.length }];
  const tabs = isSearchActive ? searchTabs : defaultTabs;

  const isLoading = isSearchActive
    ? searchLoading
    : (currentTab === 'stock' && shippedLoading) ||
      (currentTab === 'registered' && activeLoading) ||
      (currentTab === 'orders' && nanoShipmentsLoading);
  const queryError = isSearchActive
    ? undefined
    : currentTab === 'stock'
      ? shippedError
      : currentTab === 'orders'
        ? nanoShipmentsError
        : activeError;
  const showQueryError = queryError && (currentTab === 'orders' ? !nanoShipmentsData : !shippedData && !activeData);

  // Only show full-page spinner on true initial load (no data at all).
  // During pagination transitions, stale data is kept visible via previousData.
  const initialLoading =
    isLoading &&
    !isSearchActive &&
    ((currentTab === 'orders' && !nanoShipmentsData) || (currentTab !== 'orders' && !shippedData && !activeData));
  // Show the dim overlay when we have stale rows and a network request is in flight.
  const isTransitioning =
    isLoading &&
    !initialLoading &&
    (isSearchActive || (currentTab === 'stock' && !shippedRawData) || (currentTab === 'registered' && !activeRawData));

  const currentPage = currentTab === 'stock' ? stockPage : registeredPage;
  const currentTotal = currentTab === 'stock' ? shippedTotal : activeTotal;
  const totalPages = Math.ceil(currentTotal / PAGE_SIZE);

  function handlePageChange(page: number) {
    if (currentTab === 'stock') setStockPage(page);
    else setRegisteredPage(page);
  }

  function handleTabChange(key: string) {
    setCurrentTab(key as ChipTab);
    setSearchInput('');
    setDebouncedQuery('');
    setHasExternalMatch(false);
    updateUrl('');
  }

  // Modal state
  const [registerOpen, setRegisterOpen] = useState(false);
  const [registerInitialId, setRegisterInitialId] = useState('');
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferChip, setTransferChip] = useState<Chip | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailChip, setDetailChip] = useState<Chip | null>(null);
  // The mini list chip lacks the owner's full contact info, so editing waits for the detail fetch.
  const [detailLoaded, setDetailLoaded] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editChip, setEditChip] = useState<Chip | null>(null);
  const [editPermissions, setEditPermissions] = useState({ canEditPet: false, canEditOwner: false });
  const [recallOpen, setRecallOpen] = useState(false);
  const [recallChipState, setRecallChipState] = useState<Chip | null>(null);
  const [removeFromPetOpen, setRemoveFromPetOpen] = useState(false);
  const [removeFromPetChipState, setRemoveFromPetChipState] = useState<Chip | null>(null);
  const [changeConditionOpen, setChangeConditionOpen] = useState(false);
  const [changeConditionChip, setChangeConditionChip] = useState<Chip | null>(null);
  const [extDetailOpen, setExtDetailOpen] = useState(false);
  const [extDetailChip, setExtDetailChip] = useState<ExternalChip | null>(null);
  const [claimOpen, setClaimOpen] = useState(false);
  const [claimChip, setClaimChip] = useState<ExternalChip | null>(null);
  const [orderNanosOpen, setOrderNanosOpen] = useState(false);
  // Mirrors `DevicesEnrollModal` state pattern: the enrollment prompt opens
  // automatically for unverified users on first view; once dismissed it stays
  // closed for the rest of the page lifetime and the empty state's CTA
  // re-opens it.
  const [enrollModalDismissed, setEnrollModalDismissed] = useState(false);

  // Memoize the device hand-off prop for TransferChipModal so the modal sees a
  // stable object reference until the underlying chip/pet pairing changes.
  const transferDevice = useMemo(() => {
    if (!transferChip) return null;
    const device = petByChipGqlId.get(transferChip._graphqlId)?.device;
    return device ? { moduleId: device.moduleId } : null;
  }, [transferChip, petByChipGqlId]);

  // ── Modal handlers ──────────────────────────

  function handleRegister(chipId: string) {
    setRegisterInitialId(chipId);
    setRegisterOpen(true);
    trackChipRegistrationStarted(chipId ? 'stock_tab' : 'header_button');
  }

  async function handleRegisterSubmit({
    graphqlId,
    chipId,
    serialNumber,
    needsClaim,
    petData,
    breedId,
    adopterData,
    existingPetId,
  }: RegisterChipPayload) {
    // Reassignment may mint a new graphql id — adopt it for the rest of the
    // flow so the pet/owner mutations operate on the post-claim chip, matching
    // AddChipToPetModal. Stable in practice today, but the two flows should
    // agree.
    let resolvedChipId = graphqlId;
    if (needsClaim) {
      const { data } = await reassignToReseller({
        variables: { input: { serialNumber, clearOwnerInfo: false } },
      });
      const reassignedId = data?.reassignChipToReseller?.id;
      if (reassignedId) {
        resolvedChipId = reassignedId;
        // Clear just the owner while preserving pet info.
        await recallChipMut({ variables: { input: { chipId: resolvedChipId } } });
      }
    }

    if (petData) {
      await assignNewPet({
        variables: {
          input: {
            chipId: resolvedChipId,
            petInfo: mapPetFormToChipNewPetInput(petData, breedId),
          },
        },
      });
    } else if (existingPetId) {
      await assignExistingPet({
        variables: { input: { chipId: resolvedChipId, petId: existingPetId } },
      });
    }

    if (adopterData) {
      await assignNewOwner({
        variables: {
          input: {
            chipId: resolvedChipId,
            contactInfo: mapAdopterFormToContactInfoInput(adopterData),
          },
        },
      });
      showToast(`Chip ${chipId} registered with owner successfully`);
    } else {
      showToast(`Chip ${chipId} registered successfully`);
    }
    // `hasOwner` reflects whether an owner contact was attached on this call —
    // not whether a pet was. Keep `species` from the new-pet form when available;
    // existing-pet path doesn't thread species through the callback today.
    trackChipRegistered(!!adopterData, petData?.species);
  }

  async function handleDetail(chip: Chip) {
    setDetailChip(chip);
    setDetailLoaded(false);
    setDetailOpen(true);

    try {
      const { data } = await fetchChipDetails({ variables: { id: chip._graphqlId } });
      if (data?.resellerChip) {
        const email = data.currentUser?.email ?? resellerEmail;
        setDetailChip(mapResellerChipToRescueChip(data.resellerChip, email));
        setDetailLoaded(true);
      }
    } catch {
      // Keep showing the mini chip data we already have
    }
  }

  function handleTransfer(chip: Chip) {
    setTransferChip(chip);
    setTransferOpen(true);
  }

  async function handleTransferSubmit(chipId: string, adopter: AdopterFormData, deviceAction?: DeviceTransferAction) {
    if (!transferChip) {
      throw new Error(`No chip selected for transfer (chipId=${chipId})`);
    }

    const petInfo = petByChipGqlId.get(transferChip._graphqlId);

    if (deviceAction === 'transfer' && petInfo?.device) {
      const moduleId = petInfo.device.moduleId;
      let result;
      try {
        result = await transferPetWithChip({
          petId: petInfo.petId,
          chipId: transferChip._graphqlId,
          adopter,
        });
      } catch (err) {
        const detail = err instanceof Error ? err.message : 'Unknown error';
        showToast(`Failed to add owner: ${detail}`);
        throw err;
      }
      trackDeviceTransferToAdopt('chips_page_add_owner');
      showToast(`Transfer initiated for ${moduleId}`);
      if (result.chipError) {
        throw DeviceAppliedChipFailedError.fromDeviceAction(
          `${moduleId} adoption transfer was initiated`,
          result.chipError instanceof Error ? result.chipError.message : 'Unknown error',
        );
      }
      trackChipOwnerAdded('chips_page');
      return;
    }

    let deviceApplied: { description: string } | null = null;
    try {
      if (deviceAction === 'return' && petInfo?.device) {
        await unassignDeviceMut({
          variables: { moduleId: petInfo.device.moduleId, petId: petInfo.petId },
        });
        showToast(`${petInfo.device.moduleId} returned to rescue stock`);
        deviceApplied = { description: `${petInfo.device.moduleId} was returned to rescue stock` };
      }

      await assignNewOwner({
        variables: {
          input: {
            chipId: transferChip._graphqlId,
            contactInfo: mapAdopterFormToContactInfoInput(adopter),
          },
        },
      });
      trackChipOwnerAdded('chips_page');
    } catch (err) {
      const detail = err instanceof Error ? err.message : 'Unknown error';
      if (deviceApplied) {
        throw DeviceAppliedChipFailedError.fromDeviceAction(deviceApplied.description, detail);
      }
      showToast(`Failed to add owner: ${detail}`);
      throw err;
    }
  }

  function handleRecall(chip: Chip) {
    setRecallChipState(chip);
    setRecallOpen(true);
  }

  function handleChangeCondition(chip: Chip) {
    setChangeConditionChip(chip);
    setChangeConditionOpen(true);
  }

  async function handleChangeConditionSubmit(chip: Chip, condition: ChipCondition) {
    try {
      await updateChipCondition({
        variables: { chipId: chip._graphqlId, chipCondition: condition },
      });
      // Refetch every observed chip-list query so the removed chip disappears
      // from the user's current page (any offset) and from active searches,
      // and update the Dashboard stat cards while we're at it.
      await apolloClient.refetchQueries({
        include: [rescueViewingChipsQuery, rescueSearchChipsQuery, rescueChipStatsQuery],
      });
      trackChipConditionUpdated(condition);
      showToast(`Chip ${chip.id} removed: ${chipConditionBadgeLabel(condition)}`);
    } catch (err) {
      showToast(`Failed to update chip: ${err instanceof Error ? err.message : 'Unknown error'}`);
      throw err;
    }
  }

  async function handleRecallSubmit(chipId: string) {
    if (!recallChipState) {
      return;
    }

    try {
      await recallChipMut({
        variables: { input: { chipId: recallChipState._graphqlId } },
      });
      showToast(`Owner cleared and chip ${chipId} recalled successfully`);
    } catch (err) {
      showToast(`Failed to recall chip: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  function chipEditPermissions(chip: Chip | null) {
    if (!chip || !detailLoaded) {
      return { canEditPet: false, canEditOwner: false };
    }
    const petId = chip.petId;
    const adoptionLocked =
      !!petId &&
      (adoptedLoading || pendingAdoptionLoading || adoptedPetIds.has(petId) || pendingAdoptionPetIds.has(petId));
    if (adoptionLocked) {
      return { canEditPet: false, canEditOwner: false };
    }
    return { canEditPet: !!petId && rescuePetIds.has(petId), canEditOwner: !!chip.adopterFirstName };
  }

  const detailEditPermissions = chipEditPermissions(detailChip);

  function handleEdit(chip: Chip) {
    setEditChip(chip);
    setEditPermissions(detailEditPermissions);
    setEditOpen(true);
  }

  async function handleEditSubmit(chip: Chip, { originalPet, pet, adopter }: EditChipPayload) {
    let petSaved = false;
    try {
      const petInput = pet && chip.petId ? mapPetFormToUpdatePetInput(chip.petId, originalPet, pet) : null;
      if (petInput) {
        await updatePet({ variables: { input: petInput } });
        petSaved = true;
      }
      if (adopter) {
        await updateChipOwnerContactInfo({
          variables: {
            input: { chipId: chip._graphqlId, contactInfo: mapAdopterFormToContactInfoInput(adopter) },
          },
        });
      }
      await apolloClient.refetchQueries({
        include: [...refetchChipLists.map((q) => q.query), rescueSearchChipsQuery],
      });
      trackChipEdited(!!petInput, !!adopter);
      showToast(`Chip ${chip.id} updated`);
    } catch (err) {
      const detail = err instanceof Error ? err.message : 'Unknown error';
      if (petSaved) {
        showToast(`Pet saved, but the owner update failed: ${detail}`);
        await apolloClient.refetchQueries({
          include: [...refetchChipLists.map((q) => q.query), rescueSearchChipsQuery],
        });
      } else {
        showToast(`Failed to update chip: ${detail}`);
      }
      throw err;
    }
  }

  function handleRemoveFromPet(chip: Chip) {
    setRemoveFromPetChipState(chip);
    setRemoveFromPetOpen(true);
  }

  async function handleRemoveFromPetSubmit(chipId: string) {
    if (!removeFromPetChipState) {
      return;
    }

    try {
      await removeChipFromPetMut({
        variables: { input: { chipId: removeFromPetChipState._graphqlId } },
      });
      trackChipRemovedFromPet();
      showToast(`Chip ${chipId} removed from pet and returned to stock`);
    } catch (err) {
      showToast(`Failed to remove chip from pet: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  function handleAddedToStock() {
    showToast('Chip added to stock successfully');
    // Refetch chip lists *and* the active search query so the newly added chip
    // appears immediately, whether the user is browsing tabs or filtering by search.
    apolloClient.refetchQueries({
      include: [...refetchChipLists.map((q) => q.query), rescueSearchChipsQuery],
    });
  }

  function handleOrderChips() {
    trackChipsOrderClicked();
    setOrderNanosOpen(true);
  }

  function handleViewExternal(chip: ExternalChip) {
    setExtDetailChip(chip);
    setExtDetailOpen(true);
  }

  function handleClaim(chip: ExternalChip) {
    setClaimChip(chip);
    setClaimOpen(true);
  }

  async function handleClaimSubmit(chip: ExternalChip, keepOwner: boolean) {
    try {
      // Step 1: Reassign chip to our org. Pass clearOwnerInfo: false so chips
      // that have a contact entry keep both pet and owner. Chips with no
      // contact entry will have their pet cleared by the backend regardless.
      const { data } = await reassignToReseller({
        variables: {
          input: {
            serialNumber: chip.fullChipId,
            clearOwnerInfo: false,
          },
        },
      });
      // Step 2: If the user opted to clear the owner — and there was actually
      // an owner to clear — recallChip clears just the owner while preserving
      // pet info.
      if (!keepOwner && chip.ownerName) {
        const reassignedId = data?.reassignChipToReseller?.id;
        if (reassignedId) {
          await recallChipMut({ variables: { input: { chipId: reassignedId } } });
        }
      }

      // Refetch the active search query so the newly-claimed chip shows up in
      // the search results pane immediately. The mutations only refetch the
      // stock lists; the search query has its own cache key.
      apolloClient.refetchQueries({ include: [rescueSearchChipsQuery] });

      showToast(`Chip ${chip.id} claimed successfully`);
    } catch (err) {
      showToast(`Failed to claim chip: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  // Hold the page header alone (no table, no modal) until `rescueOrgInfoQuery`
  // resolves. `isReseller` defaults to `false` during loading, so rendering the
  // gate here would flash "Not enrolled" + the auto-opening modal at enrolled
  // partners on cold-load / direct-link entry.
  if (orgInfoLoading) {
    return (
      <div>
        <div className={styles.pageHeader}>
          <h1>Fi Nano Microchips</h1>
        </div>
        <LoadingSpinner />
      </div>
    );
  }

  // Enrollment gate: a rescue with no reseller account sees the enrollment empty state +
  // picture-cover modal (which opens by default and is re-opened by the empty-state CTA after
  // dismissal). A reseller still awaiting verification keeps the full chip list, matching the
  // legacy flow, with a pending banner above it.
  if (!isReseller) {
    return (
      <div>
        <div className={styles.pageHeader}>
          <h1>Fi Nano Microchips</h1>
        </div>
        <EnrollEmptyState
          description="Not enrolled yet. Get free Fi Nano microchips to chip every pet in your care and register adopters in seconds."
          ctaLabel="Set up now"
          onCta={() => setEnrollModalDismissed(false)}
        />
        <ChipsEnrollModal open={!enrollModalDismissed} onClose={() => setEnrollModalDismissed(true)} />
      </div>
    );
  }

  return (
    <div>
      {/* Page header */}
      <div className={styles.pageHeader}>
        <h1>Fi Nano Microchips</h1>
        <div className={styles.headerActions}>
          <div className={styles.orderNanosWrapper}>
            <Button variant="green" onClick={handleOrderChips}>
              <svg
                className={styles.headerBtnIcon}
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="9" cy="21" r="1" />
                <circle cx="20" cy="21" r="1" />
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
              </svg>
              Order Fi Nanos
            </Button>
            <span className={styles.orderNanosSubtitle}>Free for all rescues</span>
          </div>
        </div>
      </div>

      {isRescueReseller && !isVerifiedReseller && <ChipsPendingBanner />}

      {/* Search */}
      <SearchBar
        value={searchInput}
        onChange={handleSearchChange}
        placeholder="Search by chip ID, pet name, or owner..."
      />

      {/* Tabs + Register Chip */}
      <div className={styles.tabsRow}>
        <PortalTabs tabs={tabs} activeTab={isSearchActive ? 'all' : currentTab} onTabChange={handleTabChange} />
      </div>

      {/* Content */}
      {initialLoading ? (
        <LoadingSpinner message={currentTab === 'orders' ? 'Loading orders...' : 'Loading chips...'} />
      ) : showQueryError ? (
        <div className={styles.errorContainer}>
          <p>Failed to load {currentTab === 'orders' ? 'orders' : 'chips'}. Please try again.</p>
        </div>
      ) : currentTab === 'orders' && !isSearchActive ? (
        <OrdersTable shipments={nanoShipments} />
      ) : (
        <>
          <div className={isTransitioning ? styles.tableTransitioning : undefined}>
            <ChipsTable
              chips={displayedChips}
              tab={isSearchActive ? 'all' : currentTab}
              searchQuery={searchInput}
              searchLoading={isSearchActive && searchLoading}
              adoptedPetIds={adoptedPetIds}
              adoptedLoading={adoptedLoading}
              onRegister={handleRegister}
              onDetail={handleDetail}
              onTransfer={handleTransfer}
              onRecall={handleRecall}
              externalLookupSlot={
                <ExternalChipLookup
                  isVerifiedReseller={isVerifiedReseller}
                  searchQuery={debouncedQuery.trim()}
                  onViewExternal={handleViewExternal}
                  onAddedToStock={handleAddedToStock}
                  onMatchChange={setHasExternalMatch}
                />
              }
            />
          </div>
          {!isSearchActive && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={currentTotal}
              pageSize={PAGE_SIZE}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}

      {/* Info banner */}
      <div className={styles.infoBanner}>
        <svg
          className={styles.infoBannerIcon}
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
        <span>
          Fi Nano microchips can be re-ordered for free by Fi rescue partners.{' '}
          <a href="/rescue/help" className={styles.infoBannerLink}>
            Learn more
          </a>
        </span>
      </div>

      {/* Modals */}
      <RegisterChipModal
        key={registerInitialId}
        open={registerOpen}
        onClose={() => setRegisterOpen(false)}
        initialChipId={registerInitialId}
        onRegister={handleRegisterSubmit}
      />
      <TransferChipModal
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        chip={transferChip}
        device={transferDevice}
        onTransfer={handleTransferSubmit}
      />
      <ChipDetailModal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        chip={detailChip}
        petAdopted={!!detailChip?.petId && (adoptedLoading || adoptedPetIds.has(detailChip.petId))}
        petPendingAdoption={
          !!detailChip?.petId && (pendingAdoptionLoading || pendingAdoptionPetIds.has(detailChip.petId))
        }
        onTransfer={handleTransfer}
        onRecall={handleRecall}
        onRemoveFromPet={handleRemoveFromPet}
        onChangeCondition={handleChangeCondition}
        canEdit={detailEditPermissions.canEditPet || detailEditPermissions.canEditOwner}
        onEdit={handleEdit}
      />
      <EditChipModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        chip={editChip}
        canEditPet={editPermissions.canEditPet}
        canEditOwner={editPermissions.canEditOwner}
        onSave={handleEditSubmit}
      />
      <RecallModal
        open={recallOpen}
        onClose={() => setRecallOpen(false)}
        chip={recallChipState}
        onRecall={handleRecallSubmit}
      />
      <RemoveChipFromPetModal
        open={removeFromPetOpen}
        onClose={() => setRemoveFromPetOpen(false)}
        chip={removeFromPetChipState}
        onRemove={handleRemoveFromPetSubmit}
      />
      <ChangeConditionModal
        open={changeConditionOpen}
        onClose={() => setChangeConditionOpen(false)}
        chip={changeConditionChip}
        onConfirm={handleChangeConditionSubmit}
      />
      <ExternalChipDetailModal
        open={extDetailOpen}
        onClose={() => setExtDetailOpen(false)}
        chip={extDetailChip}
        onClaim={handleClaim}
      />
      <ClaimChipModal
        open={claimOpen}
        onClose={() => setClaimOpen(false)}
        chip={claimChip}
        onClaim={handleClaimSubmit}
      />
      <OrderNanosModal open={orderNanosOpen} onClose={() => setOrderNanosOpen(false)} />
    </div>
  );
}
